/**
 * ADR-180 — the read-only view of a scheduled successor contract, as rows.
 *
 * The server (GET …/renewals/:eventId/preview) resolves the successor the way
 * the switch will and says where each value comes from; this module only
 * turns that answer into labelled rows, so the dialog stays a renderer and the
 * wording is testable without React.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const TERM_SOURCES = Object.freeze({
  SCHEDULED: 'scheduled',
  INHERITED: 'inherited',
  DEFAULT: 'default',
  NOT_APPLIED: 'notApplied'
});

// The note under a value when the schedule did not set it: where the switch
// takes it from. Rendered on the same line as "changes from X", so every
// "where does this come from" fact sits in one place. (Was a chip beside the
// value — "Same as this contract" read like a comparison, not a source.)
export const SOURCE_NOTES = Object.freeze({
  [TERM_SOURCES.SCHEDULED]: null, // set on the schedule — the change note, if any, says the rest
  // "the replaced contract", never "this contract": the note sits in the NEW
  // contract section, where "this" reads as the new one.
  [TERM_SOURCES.INHERITED]: 'carried over from the replaced contract',
  [TERM_SOURCES.DEFAULT]: 'the default — not set on the schedule, and the replaced contract has none to carry over',
  [TERM_SOURCES.NOT_APPLIED]: 'not applied — this is not the primary contract'
});

export const previewDayFmt = (ymd) => (ymd ? dayjs.utc(ymd).format('DD/MM/YYYY') : null);

const companyName = (value) => (value?.name
  ? `${value.name}${value.country ? ` (${value.country})` : ''}`
  : null);

const valueText = (value, nameOf = (v) => v?.name ?? null) => {
  if (!value) {
    return '—';
  }
  if (value.missing) {
    return 'No longer exists';
  }
  return nameOf(value) || '—';
};

/**
 * "changes from X" when the successor's value differs from what the replaced contract
 * (or the person, for person facts) has today. Only for values the switch
 * will actually write.
 */
const changeNote = (value, nameOf = (v) => v?.name ?? null) => {
  if (!value || !value.current || value.source === TERM_SOURCES.NOT_APPLIED ||
      value.source === TERM_SOURCES.INHERITED) {
    return null;
  }
  const nextId = value.cleared ? null : value.id;
  if (nextId === (value.current.id ?? null)) {
    return null;
  }
  const from = nameOf(value.current);
  return from ? `changes from ${from}` : 'new';
};

const termRow = (label, value, nameOf) => ({
  label,
  value: valueText(value, nameOf),
  note: (value?.source ? SOURCE_NOTES[value.source] : null) || changeNote(value, nameOf),
  missing: Boolean(value?.missing)
});

/** @returns {{ title, subtitle, sections: Array<{ title, rows?, note? }>, warnings }} */
export const successorPreviewView = (preview) => {
  if (!preview) {
    return null;
  }
  const { switch: sw = {}, contract = {}, personFacts = {}, handling = {} } = preview;

  const start = previewDayFmt(sw.contractStartDate);
  const switchRows = [
    { label: "Replaced contract's last day", value: previewDayFmt(sw.cutoverDate) || '—' },
    { label: 'Switch runs', value: sw.enactmentDate ? `${previewDayFmt(sw.enactmentDate)}, automatically in the morning` : '—' },
    {
      label: 'New contract starts',
      value: start || '—',
      note: sw.gapDays > 0 ? `${sw.gapDays} day${sw.gapDays === 1 ? '' : 's'} after the switch — not in force until then` : null
    }
  ];

  const end = previewDayFmt(contract.contractEndDate);
  const contractRows = [
    termRow('Contract Type', contract.contractTypeId),
    termRow('Contract Company', contract.contractCompanyId, companyName),
    termRow('Engagement Model', contract.engagementModelId),
    { label: 'Contract Number', value: contract.contractNumber || 'No number' },
    { label: 'Contract Start Date', value: start || '—' },
    { label: 'Contract End Date', value: end || 'Open-ended' },
    ...(end && contract.contractEndReason ? [{ label: 'Fixed-term comment', value: contract.contractEndReason }] : [])
  ];

  const personSection = personFacts.applies === false
    ? {
      title: 'Person details',
      note: 'Not changed — this is not the primary contract, so position, grade, working location and cluster stay as they are.'
    }
    : {
      title: 'Person details',
      rows: [
        termRow('Position', personFacts.positionId),
        personFacts.gradeId?.cleared
          ? { label: 'Grade', value: 'None (the position has no grade)', note: personFacts.gradeId.current?.name ? `changes from ${personFacts.gradeId.current.name}` : null }
          : termRow('Grade', personFacts.gradeId),
        termRow('Working Location', personFacts.workingLocationId),
        termRow('Cluster', personFacts.clusterId),
        ...(personFacts.hireDate?.value
          ? [{
            label: 'Hire Date',
            value: previewDayFmt(personFacts.hireDate.value),
            note: personFacts.hireDate.value !== personFacts.hireDate.current
              ? `changes from ${previewDayFmt(personFacts.hireDate.current) || '—'}`
              : null
          }]
          : [])
      ]
    };

  const handlingRows = [
    {
      label: 'Projects',
      value: handling.keepProjects === true
        ? 'Kept on the new contract'
        : (handling.keepProjects === false ? 'Ended with the replaced contract' : '—')
    },
    ...(handling.withdrawScheduledEnd ? [{ label: 'Scheduled end', value: "The replaced contract's scheduled end is withdrawn first" }] : [])
  ];

  const raisedBy = preview.raisedBy?.displayName || preview.raisedBy?.email || preview.raisedBy?.id || '—';
  const raisedAt = preview.raisedAt ? dayjs(preview.raisedAt).format('DD/MM/YYYY') : null;
  const whyRows = [
    { label: 'Reason', value: preview.reason || '—' },
    ...(preview.comments ? [{ label: 'Comments', value: preview.comments }] : []),
    { label: 'Scheduled by', value: raisedAt ? `${raisedBy} on ${raisedAt}` : raisedBy }
  ];

  return {
    title: 'Successor contract',
    subtitle: start
      ? `Starts ${start}, ${end ? `fixed term until ${end}` : 'open-ended'}`
      : null,
    sections: [
      { title: 'The switch', rows: switchRows },
      { title: 'New contract', rows: contractRows },
      personSection,
      { title: 'Handling', rows: handlingRows },
      { title: 'Why', rows: whyRows }
    ],
    warnings: Array.isArray(preview.warnings) ? preview.warnings : []
  };
};
